import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { REFERRER_TYPE } from '../../../constants/gaEnums';
import { useSearchParams } from 'react-router-dom';
import { Box, Divider, Grid, Stack, useMediaQuery } from '@mui/material';
import theme from '../../../theme';
import PlaylistVerticalCard from '../../../components/card/PlaylistVerticalCard';
import PlaylistCard from '../../../components/card/PlaylistCard';
import Pagination from '../../../components/common/Pagination';
import ProgressBar from '../../../components/common/ProgressBar';
import NoData from '../../../components/common/NoData';
import SubTab from '../../../components/button/SubTab';
import SelectFilter from '../../../components/common/SelectFilter';

const SUB_TABS = [
  { label: '곡 플리', key: 'songs' },
  { label: '앨범 플리', key: 'albums' },
];

const SORT_OPTIONS = [
  { label: '최신순', value: 'latest' },
  { label: '인기순', value: 'popular' },
];

// 10줄마다 페이지네이션 (xs: 1열=10개, md/lg: 4열=40개)
const ITEMS_PER_PAGE = { xs: 10, md: 40 };

const DEFAULT_TYPE = 'songs';
const DEFAULT_SORT = 'latest';
const TYPE_KEYS = SUB_TABS.map((t) => t.key);
const SORT_KEYS = SORT_OPTIONS.map((o) => o.value);

const ListTab = ({ api, userId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);

  const isXs = useMediaQuery(theme.breakpoints.down('md'));

  // URL에서 상태 읽기
  const urlType = searchParams.get('type');
  const urlSort = searchParams.get('sort');
  const typeTab = TYPE_KEYS.includes(urlType) ? urlType : DEFAULT_TYPE;
  const sortBy = SORT_KEYS.includes(urlSort) ? urlSort : DEFAULT_SORT;
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const itemsPerPage = isXs ? ITEMS_PER_PAGE.xs : ITEMS_PER_PAGE.md;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await (userId ? api(userId) : api());
      setAllData(response.data || []);
    } catch (err) {
      console.error('Failed to fetch list data:', err);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 카운트 계산
  const countByType = useMemo(
    () => ({
      songs: allData.filter((item) => item.type === 'TRACK').length,
      albums: allData.filter((item) => item.type === 'ALBUM').length,
    }),
    [allData],
  );

  const subTabsWithCount = useMemo(
    () =>
      SUB_TABS.map((tab) => ({
        ...tab,
        label: `${tab.label} (${countByType[tab.key] ?? 0})`,
      })),
    [countByType],
  );

  // 필터링 + 정렬 + 페이지네이션
  const filteredData = useMemo(() => {
    const typeFilter = typeTab === 'songs' ? 'TRACK' : 'ALBUM';
    return allData.filter((item) => item.type === typeFilter);
  }, [allData, typeTab]);

  const sortedData = useMemo(() => {
    if (sortBy === 'popular') {
      return [...filteredData].sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
    }
    return filteredData;
  }, [filteredData, sortBy]);

  const pageCount = useMemo(
    () => Math.ceil(sortedData.length / itemsPerPage),
    [sortedData, itemsPerPage],
  );

  const safePage = pageCount > 0 ? Math.min(currentPage, pageCount) : 1;

  const paginatedData = useMemo(() => {
    const startIndex = (safePage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, safePage, itemsPerPage]);

  const handleTypeTabChange = (newType) => {
    setSearchParams({ type: newType, sort: DEFAULT_SORT, page: '1' });
  };

  const handleSortChange = (newSort) => {
    setSearchParams({ type: typeTab, sort: newSort, page: '1' });
  };

  const handlePageChange = (event, value) => {
    setSearchParams({ type: typeTab, sort: sortBy, page: value.toString() });
  };

  return (
    <ProgressBar isLoading={loading}>
      {/* 서브탭 + 정렬 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: '16px', md: '20px', lg: '30px' },
        }}
      >
        <SubTab tabs={subTabsWithCount} activeTab={typeTab} onTabChange={handleTypeTabChange} />
        <SelectFilter
          label="정렬"
          value={sortBy}
          onChange={handleSortChange}
          options={SORT_OPTIONS}
        />
      </Box>
      {paginatedData.length === 0 && !loading ? (
        <NoData />
      ) : (
        <>
          {isXs ? (
            <Stack spacing={'15px'}>
              {paginatedData.map((playlist, index) => (
                <React.Fragment key={playlist.id}>
                  <Box>
                    <PlaylistCard
                      playlist={playlist}
                      dateOnNewLine
                      hideUser
                      referrerType={REFERRER_TYPE.MYPAGE}
                      thumbnailVariant="square"
                    />
                    <Divider sx={{ borderColor: theme.palette.grey.line, mt: '15px' }} />
                  </Box>
                </React.Fragment>
              ))}
            </Stack>
          ) : (
            <Box sx={{ overflow: 'hidden' }}>
              <Grid container columnSpacing={{ md: '16px', lg: '20px' }} rowSpacing={'40px'}>
                {paginatedData.map((playlist, index) => (
                  <React.Fragment key={playlist.id}>
                    <Grid item xs={12} md={3}>
                      <PlaylistVerticalCard
                        playlist={playlist}
                        hideUser
                        referrerType={REFERRER_TYPE.MYPAGE}
                        thumbnailVariant="square"
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          )}

          {pageCount > 1 && (
            <Pagination count={pageCount} page={safePage} onChange={handlePageChange} />
          )}
        </>
      )}
    </ProgressBar>
  );
};

export default ListTab;
